/**
 * The focus model inside a dashboard section (spec §1.6; card D1, D-076). The shell turns keys into focus inputs
 * (`keyToDashFocusInput`: arrows or WASD move, F secondary, R context, PgUp/PgDn page) and hands them to the open
 * section through a `DashNavPort`; the section runs the shared station focus model (`ui/nav.ts`) through a
 * `DashFocus` (pure, Node-testable) and renders `FocusButton`s from `ui/focus.tsx` under its `FocusContext`.
 * Enter/Space (activate) reach the section first at the list and detail levels; unhandled, the shell goes a level in.
 * Inputs arrive on key events only (nothing per frame).
 */
import { focusAt, focusKey, initialFocus, navigate, normalizeFocus, type FocusGroup, type FocusState } from '../nav'
import type { FocusApi, FocusHandlers } from '../focus'

export type DashFocusInput = 'up' | 'down' | 'left' | 'right' | 'activate' | 'secondary' | 'context' | 'pageUp' | 'pageDown'

/** Keys the open section may take (null = not a focus key). Directions and paging repeat; actions do not. */
export function keyToDashFocusInput(code: string, repeat: boolean): DashFocusInput | null {
  switch (code) {
    case 'ArrowUp':
    case 'KeyW':
      return 'up'
    case 'ArrowDown':
    case 'KeyS':
      return 'down'
    case 'ArrowLeft':
    case 'KeyA':
      return 'left'
    case 'ArrowRight':
    case 'KeyD':
      return 'right'
    case 'PageUp':
      return 'pageUp'
    case 'PageDown':
      return 'pageDown'
    case 'KeyF':
      return repeat ? null : 'secondary'
    case 'KeyR':
      return repeat ? null : 'context'
    default:
      return null
  }
}

/** The shell → section channel: the section subscribes while mounted; a listener returns true when it used the input. */
export interface DashNavPort {
  dispatch(input: DashFocusInput): boolean
  subscribe(listener: (input: DashFocusInput) => boolean): () => void
}

export function createDashNavPort(): DashNavPort {
  const listeners: ((input: DashFocusInput) => boolean)[] = []
  return {
    dispatch(input: DashFocusInput): boolean {
      let used = false
      for (let i = 0; i < listeners.length; i++) if (listeners[i](input)) used = true
      return used
    },
    subscribe(listener: (input: DashFocusInput) => boolean): () => void {
      listeners.push(listener)
      return (): void => {
        const i = listeners.indexOf(listener)
        if (i >= 0) listeners.splice(i, 1)
      }
    },
  }
}

/** Handlers a focusable item registers (`ui/focus.tsx`: activate, adjust and the dashboard's F and R actions). */
export type DashFocusHandlers = FocusHandlers

/** Moves the focus `rows` rows within its group (PgUp/PgDn), clamped to the first/last item. */
export function pageFocus(groups: readonly FocusGroup[], current: FocusState, rows: number): FocusState {
  const focus = normalizeFocus(groups, current)
  const g = groups.find((x) => x.id === focus.group)
  if (g === undefined || g.count <= 0) return focus
  const cols = g.cols !== undefined && g.cols >= 1 ? Math.floor(g.cols) : 1
  return focusAt(groups, focus, g.id, Math.max(0, Math.min(g.count - 1, focus.index + rows * cols)))
}

export interface DashFocusOptions {
  /** The section's focus groups now (lists change as data changes). */
  groups(): readonly FocusGroup[]
  /** Rows a PgUp/PgDn moves (`dashboard.json focus.pageRows`). */
  pageRows: number
  /** The focus moved (the section re-renders). */
  onChange(): void
}

export interface DashFocus extends FocusApi {
  readonly state: FocusState
  /** One input from the shell; true when it was used. */
  handle(input: DashFocusInput): boolean
  register(key: string, handlers: DashFocusHandlers): () => void
}

/** A section's focus: the pure model (`navigate`) plus the item handlers registered by `useFocusable`. */
export function createDashFocus(o: DashFocusOptions): DashFocus {
  const handlers = new Map<string, DashFocusHandlers>()
  let state = initialFocus(o.groups())
  const set = (next: FocusState): boolean => {
    if (next === state) return false
    state = next
    o.onChange()
    return true
  }
  const current = (): DashFocusHandlers | undefined => {
    state = normalizeFocus(o.groups(), state)
    return handlers.get(focusKey(state.group, state.index))
  }
  const f: DashFocus = {
    get state(): FocusState {
      return state
    },
    get focused(): string {
      return focusKey(state.group, state.index)
    },
    register(key: string, h: DashFocusHandlers): () => void {
      handlers.set(key, h)
      return (): void => {
        if (handlers.get(key) === h) handlers.delete(key)
      }
    },
    hover(group: string, index: number): void {
      set(focusAt(o.groups(), state, group, index))
    },
    handle(input: DashFocusInput): boolean {
      switch (input) {
        case 'pageUp':
        case 'pageDown':
          return set(pageFocus(o.groups(), state, input === 'pageUp' ? -o.pageRows : o.pageRows))
        case 'secondary':
        case 'context': {
          const fn = current()?.[input]
          if (fn === undefined) return false
          fn()
          return true
        }
        default: {
          const step = navigate(o.groups(), state, input)
          const moved = set(step.focus)
          const r = step.result
          if (r.kind === 'activate') {
            const fn = current()?.activate
            if (fn === undefined) return false
            fn()
            return true
          }
          if (r.kind === 'adjust') {
            const fn = current()?.adjust
            if (fn === undefined) return moved
            fn(r.delta)
            return true
          }
          return moved || r.kind === 'moved'
        }
      }
    },
  }
  return f
}
